import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { ActiveHost, Session } from '@/types/api';

/**
 * Hooks für Sprint-3-Sessions (WT-301..304).
 *
 * - `useActiveSession` polled `/sessions/current` und liefert `null` bei 404.
 * - `useSessionActiveHosts` polled die ActiveHost-Liste alle 5s solange
 *   die Session aktiv ist (refetchInterval).
 * - `useStartSession` triggert den Yubikey-Touch + Passphrase-Flow.
 * - `useRevokeSession` schließt alle Master-Sockets der Session.
 * - `useKillHost` killt einen einzelnen Master-Socket.
 *
 * Die Hooks lesen / schreiben gegen den Wartungstool-Backend - sie
 * persistieren nichts im Browser-Storage. Reload überlebt: bei Mount
 * fragt die UI `/sessions/current` und fällt nicht in den Start-Mode
 * wenn das Backend noch eine Session kennt.
 */

export interface StartSessionInput {
  /** TTL in Stunden, Default 4 (Spec-Default). Backend cappt auf 24. */
  ttlHours?: number;
  /**
   * Wenn nicht-leer, werden vor Rückkehr Master-Sockets für diese
   * Host-Aliase angelegt (Yubikey-Touch pro Host).
   */
  eagerWarmupHosts?: string[];
  /** Optionale Session-Notiz (Backend: notes, max 500 Zeichen). */
  notes?: string;
  /**
   * DEPRECATED: Das Backend-Schema SessionStartRequest (extra=forbid) kennt
   * KEIN customer_ids - Eager-Warmup läuft ausschließlich über
   * host_aliases (eager_warmup_hosts). Das Feld bleibt für die bestehende
   * StartSessionCard-UI erhalten, wird aber NICHT an den Server gesendet
   * (sonst 422). Für echtes Kunden-Warmup müsste das Backend customer_ids
   * -> host_aliases auflösen (siehe Bericht).
   */
  customerIds?: string[];
}

/** Wire-Body exakt nach backend app/api/schemas/sessions.py: SessionStartRequest. */
interface StartSessionBody {
  ttl_seconds?: number;
  eager_warmup_hosts?: string[];
  notes?: string;
}

// ---------------------------------------------------------------------------
// Wire-Typen (Backend Source-of-Truth, weichen vom veralteten api.gen.ts ab)
// ---------------------------------------------------------------------------

/**
 * Backend SessionResponse (schemas/sessions.py). Wichtig: liefert
 * started_at + ttl_seconds, KEIN expires_at. Der generierte api.gen.ts-Type
 * `Session` ist veraltet, deshalb mappen wir hier von Hand.
 */
interface WireSession {
  id: string;
  started_at: string;
  stopped_at?: string | null;
  started_by_user: string;
  ttl_seconds: number;
  status: 'active' | 'expired' | 'revoked';
  notes?: string | null;
  revocation_reason?: string | null;
  host_count?: number;
}

/** Backend SessionStartResponse: { session, warmup_results }. */
interface WireSessionStartResponse {
  session: WireSession;
  warmup_results?: Array<{
    host_alias: string;
    success: boolean;
    error?: string | null;
    duration_ms?: number;
  }>;
}

/** Backend ActiveHostInfo (schemas/sessions.py). */
interface WireActiveHost {
  host_alias: string;
  alive: boolean;
  last_used_at?: string | null;
  commands_executed?: number;
  system_hostname?: string | null;
  customer_name?: string | null;
}

/** Backend ActiveHostsResponse: Wrapper-Objekt { session_id, hosts:[...] }. */
interface WireActiveHostsResponse {
  session_id: string;
  hosts: WireActiveHost[];
}

/** UI-Host inklusive Live-Socket-Status (`alive`) direkt vom Backend. */
export type ActiveHostView = ActiveHost & { alive?: boolean };

/**
 * Backend liefert started_at + ttl_seconds; die UI (LiveCountdown) erwartet
 * ein absolutes expires_at -> hier ausgerechnet.
 */
function mapSession(w: WireSession): Session {
  const expiresAt = new Date(
    new Date(w.started_at).getTime() + w.ttl_seconds * 1000
  ).toISOString();
  return {
    id: w.id,
    started_at: w.started_at,
    expires_at: expiresAt,
    status: w.status,
    active_host_count: w.host_count ?? 0,
  };
}

function mapActiveHost(w: WireActiveHost): ActiveHostView {
  // Backend ActiveHostInfo hat kein first_seen_at, und last_used_at kann null
  // sein (Socket angelegt, noch nicht benutzt). Für die reine Anzeige nutzen
  // wir last_used_at bzw. now() als Fallback; `alive` ist die Backend-Wahrheit.
  const lastUsed = w.last_used_at ?? new Date().toISOString();
  return {
    host_alias: w.host_alias,
    first_seen_at: lastUsed,
    last_used_at: lastUsed,
    command_count: w.commands_executed ?? 0,
    customer_name: w.customer_name ?? null,
    system_hostname: w.system_hostname ?? null,
    alive: w.alive,
  };
}

export function useActiveSession(): ReturnType<typeof useQuery<Session | null>> {
  return useQuery<Session | null>({
    queryKey: queryKeys.sessions.current,
    queryFn: async () => {
      try {
        const raw = await api.get<WireSession>('/sessions/current');
        return mapSession(raw);
      } catch (err) {
        // 404 == keine aktive Session, das ist Default-Zustand der UI.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    // TTL kann auch ohne UI-Action ablaufen -> moderate Hintergrund-Refetch.
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useSessionActiveHosts(
  sessionId: string | undefined,
  options: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<ActiveHostView[]>> {
  const enabled = Boolean(sessionId) && options.enabled !== false;
  return useQuery<ActiveHostView[]>({
    queryKey: queryKeys.sessions.activeHosts(sessionId ?? ''),
    queryFn: async () => {
      // Backend liefert ein Wrapper-Objekt { session_id, hosts:[...] },
      // KEIN nacktes Array. Ohne das Auspacken rendert die Tabelle nie.
      const raw = await api.get<WireActiveHostsResponse>(
        `/sessions/${sessionId ?? ''}/active-hosts`
      );
      return raw.hosts.map(mapActiveHost);
    },
    enabled,
    refetchInterval: enabled ? 5_000 : false,
    staleTime: 2_000,
  });
}

export function useStartSession(): ReturnType<
  typeof useMutation<Session, Error, StartSessionInput>
> {
  const qc = useQueryClient();
  return useMutation<Session, Error, StartSessionInput>({
    mutationFn: async (input) => {
      const body: StartSessionBody = {};
      if (input.ttlHours != null) {
        // Backend erwartet ttl_seconds (60..86400), nicht ttl_hours.
        body.ttl_seconds = Math.round(input.ttlHours * 3600);
      }
      const hosts = input.eagerWarmupHosts ?? [];
      if (hosts.length > 0) body.eager_warmup_hosts = hosts;
      if (input.notes) body.notes = input.notes;
      // customerIds werden bewusst NICHT gesendet: SessionStartRequest ist
      // extra=forbid und kennt das Feld nicht (sonst 422).
      const res = await api.post<WireSessionStartResponse, StartSessionBody>(
        '/sessions/start',
        body
      );
      // Antwort ist { session, warmup_results } - die Session steckt in .session.
      return mapSession(res.session);
    },
    onSuccess: (session) => {
      qc.setQueryData(queryKeys.sessions.current, session);
      void qc.invalidateQueries({ queryKey: queryKeys.sessions.all });
    },
  });
}

export function useRevokeSession(): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (sessionId) => api.delete<void>(`/sessions/${sessionId}`),
    onSuccess: () => {
      qc.setQueryData(queryKeys.sessions.current, null);
      void qc.invalidateQueries({ queryKey: queryKeys.sessions.all });
    },
  });
}

export interface KillHostInput {
  sessionId: string;
  hostAlias: string;
}

interface KillHostResponse {
  session_id: string;
  host_alias: string;
  killed: boolean;
}

export function useKillHost(): ReturnType<typeof useMutation<void, Error, KillHostInput>> {
  const qc = useQueryClient();
  return useMutation<void, Error, KillHostInput>({
    mutationFn: async ({ sessionId, hostAlias }) => {
      // Backend: POST /sessions/{id}/kill-host/{host_alias} (nicht DELETE
      // /active-hosts/...). host_alias-Pattern: ^[a-z0-9_-]{1,128}$.
      await api.post<KillHostResponse>(
        `/sessions/${sessionId}/kill-host/${encodeURIComponent(hostAlias)}`
      );
    },
    onSuccess: (_data, { sessionId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions.activeHosts(sessionId) });
    },
  });
}
